<script setup>
import { computed, ref, onMounted, onBeforeUnmount, nextTick } from "vue";
import { useRoute } from "vue-router";
import SidenavUpItem from "./SidenavUpItem.vue";

const sidebarWidth = ref(0);
const resizeObserver = ref(null);
const sidebarRef = ref(null);
const emit = defineEmits(["resize"]);
const route = useRoute();
const isCollapsed = computed(() => sidebarWidth.value <= 96);

const menuSections = [
  {
    title: "",
    items: [
      { to: { name: "Dashboard" }, navText: "Dashboard", icon: "fa fa-tachometer-alt", seg1: "dashboard-default" },
      { to: { name: "Planes" }, navText: "Planes", icon: "fa fa-clipboard-list", seg1: "plan" },
      { to: "/stops", navText: "Paradas", icon: "fa fa-map-marker-alt", seg1: "stops" },
      { to: "/routes", navText: "Rutas", icon: "fa fa-route", seg1: "routes" },
      { to: "/bultos", navText: "Bultos", icon: "fa fa-box", seg1: "bultos" },
      { to: "/evidence", navText: "Evidencias", icon: "fa fa-camera", seg1: "evidencias" },
      { to: "/decas", navText: "DeCAs", icon: "fa fa-file-invoice", seg1: "decas" },
    ],
  },
  {
    title: "Flota",
    items: [
      { to: "/vehicles", navText: "Vehículos", icon: "fa fa-truck", seg1: "vehicles" },
      { to: "/zones", navText: "Zonas", icon: "fa fa-draw-polygon", seg1: "zones" },
      { to: "/warehouses", navText: "Puntos habituales", icon: "fa fa-warehouse", seg1: "warehouses" },
    ],
  },
  {
    title: "En vivo",
    items: [
      { to: { name: "Flota" }, navText: "En ruta", icon: "fa fa-location-arrow", exactPath: "/live" },
      { to: "/live/analytics", navText: "Analíticas", icon: "fa fa-chart-line", exactPath: "/live/analytics" },
    ],
  },
  {
    title: "TTMM",
    items: [
      { to: "/ttmm/events", navText: "Eventos", icon: "fa fa-stream", exactPath: "/ttmm/events" },
      { to: "/ttmm/users", navText: "Mensajeros", icon: "fa fa-biking", exactPath: "/ttmm/users" },
    ],
  },
  {
    title: "Cuenta",
    items: [
      { to: "/account/profile", navText: "Configuración", icon: "fa fa-cog", exactPath: "/account/profile" },
      { to: "/account/users", navText: "Usuarios", icon: "fa fa-users", exactPath: "/account/users" },
      { to: "/account/security", navText: "Seguridad", icon: "fa fa-key", exactPath: "/account/security" },
      { to: "/account/api", navText: "Integración", icon: "fa fa-code", exactPath: "/account/api" },
    ],
  },
];

const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
};

const handleResize = (entries) => {
  for (const entry of entries) {
    nextTick(() => {
      sidebarWidth.value = entry.contentRect.width;
      emit("resize", sidebarWidth.value);
    });
  }
};

const debouncedHandleResize = debounce(handleResize, 300);

onMounted(() => {
  resizeObserver.value = new ResizeObserver(debouncedHandleResize);
  if (sidebarRef.value) resizeObserver.value.observe(sidebarRef.value);
});

onBeforeUnmount(() => {
  if (resizeObserver.value && sidebarRef.value) {
    resizeObserver.value.unobserve(sidebarRef.value);
  }
});

function isActive(item) {
  const seg1 = route.path.split("/")[1] || "";
  if (item.exactPath !== undefined) return route.path === item.exactPath;
  return seg1 === item.seg1;
}

</script>

<template>
  <div
    id="sidenav-collapse-main"
    ref="sidebarRef"
    class="collapse navbar-collapse w-auto h-auto h-100"
  >
    <ul class="navbar-nav">
      <template v-for="(section, sIdx) in menuSections" :key="section.title">
        <li
          v-if="sIdx > 0"
          class="nav-item nav-section"
          :class="isCollapsed ? 'mt-1 collapsed-divider' : 'mt-2'"
        >
          <h6
            v-if="!isCollapsed && section.title"
            class="nav-section-title text-uppercase font-weight-bolder"
          >
            <span>{{ section.title }}</span>
          </h6>
        </li>
        <li v-for="item in section.items" :key="item.navText" class="nav-item">
          <sidenav-up-item
            :to="item.to"
            :class="isActive(item) ? 'active' : ''"
            :nav-text="item.navText"
            :collapsed="isCollapsed"
          >
            <template #icon>
              <i :class="`${item.icon} text-xs`"></i>
            </template>
          </sidenav-up-item>
        </li>
      </template>
    </ul>
  </div>
</template>

<style lang="scss" scoped>
.nav-item {
  max-height: 29px !important;
}

.nav-section {
  h6 {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    margin-bottom: 0;
    position: relative;
    padding: 0 0.75rem 0.1rem;
    letter-spacing: 0.1em;
    color: #9ca3af;
    font-size: 0.66rem;
    line-height: 1;
  }

  h6::after {
    content: "";
    flex: 1;
    height: 1px;
    background: #e5e7eb;
  }

  h6 span {
    flex-shrink: 0;
  }
}

.navbar-nav .nav-item:not(.nav-section) :deep(.nav-link) {
  position: relative;
  min-height: 30px;
  padding-top: 0.13rem;
  padding-bottom: 0.13rem;
  color: #6b7280;
  border-radius: 8px;
  transition: background-color 160ms ease, color 160ms ease;
}

.navbar-nav .nav-item:not(.nav-section) :deep(.nav-link .icon) {
  width: 1.45rem !important;
  min-width: 1.45rem !important;
  color: #6b7280;
  transition: color 160ms ease;
}

.navbar-nav .nav-item:not(.nav-section) :deep(.nav-link:not(.active) .nav-link-text) {
  color: #6b7280;
  font-weight: 400;
}

.navbar-nav .nav-item:not(.nav-section) :deep(.nav-link:not(.active) i) {
  color: #6b7280 !important;
}

.navbar-nav .nav-item:not(.nav-section) :deep(.nav-link i) {
  width: 1rem;
  text-align: center;
  font-size: 0.76rem !important;
  line-height: 1;
}

.navbar-nav .nav-item:not(.nav-section) :deep(.nav-link:hover) {
  background-color: #f5f5f5 !important;
}

.navbar-nav .nav-item:not(.nav-section) :deep(.nav-link.active) {
  background-color: rgba(0, 0, 0, 0.075) !important;
}

.navbar-nav .nav-item:not(.nav-section) :deep(.nav-link.active .nav-link-text) {
  color: #111827 !important;
  font-weight: 600;
}

.navbar-nav .nav-item:not(.nav-section) :deep(.nav-link:hover .nav-link-text) {
  color: #111827 !important;
}

.navbar-nav .nav-item:not(.nav-section) :deep(.nav-link.active i) {
  color: #111827 !important;
}

.navbar-nav .nav-item:not(.nav-section) :deep(.nav-link:hover i) {
  color: #111827 !important;
}

.nav-section.collapsed-divider {
  border-top: 1px solid #e5e7eb;
  margin-left: 0.5rem;
  margin-right: 0.5rem;
}

.navbar-vertical.navbar-expand-xs {
  overflow-y: auto !important;
}
.navbar-vertical.navbar-expand-xs .navbar-collapse {
  overflow-y: auto !important;
  overflow-x: hidden !important;
  scrollbar-width: thin;
  scrollbar-color: #d1d5db transparent;
}

.navbar-vertical.navbar-expand-xs .navbar-collapse::-webkit-scrollbar {
  width: 6px;
}

.navbar-vertical.navbar-expand-xs .navbar-collapse::-webkit-scrollbar-track {
  background: transparent;
}

.navbar-vertical.navbar-expand-xs .navbar-collapse::-webkit-scrollbar-thumb {
  background-color: #d1d5db;
  border-radius: 999px;
}

.navbar-vertical.navbar-expand-xs .navbar-collapse::-webkit-scrollbar-thumb:hover {
  background-color: #9ca3af;
}

:deep(.nav-link.is-collapsed) {
  justify-content: center;
  padding-left: 0.5rem !important;
  padding-right: 0.5rem !important;
}

:deep(.nav-link.is-collapsed .nav-link-text) {
  display: none;
}

:deep(.nav-link.is-collapsed .icon) {
  margin: 0 !important;
}

</style>
